import { z } from "zod";

import { properCase } from "@/src/lib/string";
import { getResolvedPatientForDisplay } from "@/src/lib/task-patient";
import { getCoverageDisplayName } from "@/src/models/coverage";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { FileType } from "@/src/models/file";
import { getPatientPrimaryCoverage } from "@/src/models/patient";
import { Task, TaskPriority } from "@/src/models/task";

// Schema for the tasks_metadata view
export const taskMetadataSchema = z.object({
  assignedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime().nullable(),
  currentAssignedToUserId: z.string().uuid().nullable(),
  displayId: z.string().nullable(),
  ownerType: z.string().nullable(),
  patientFamilyName: z.string().nullable(),
  patientGivenName: z.string().nullable(),
  patientId: z.string().uuid().nullable(),
  previousTaskId: z.string().uuid().nullable(),
  taskPriority: z.nativeEnum(TaskPriority).nullable(),
  tags: z.array(z.string()).nullable(),
  searchText: z.string().nullable(),
  status: z.string().nullable(),
  source: z.string().nullable(),
  type: z.string().nullable(),
  updatedAt: z.string().datetime().nullable(),
  workflowCreatedAt: z.string().datetime().nullable(),
  note: z.string().nullable(),
  orderDescription: z.string().nullable(),
  insurance: z.string().nullable(),
  documentClassificationSummary: z.string().nullable().optional(),
  documentClassificationSlugs: z.array(z.string()).nullable().optional(),
  hasMultiplePatients: z.boolean().optional(),
});

export type TaskMetadata = z.infer<typeof taskMetadataSchema> & DatabaseDefaults;

export function taskToTaskMetadata(task: Task): TaskMetadata {
  const inboundFile = task?.files?.find((file) => file.type === FileType.INBOUND);
  const patient = getResolvedPatientForDisplay(task);

  return {
    id: task.id,
    displayId: task.displayId,
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    deletedAt: task.deletedAt,
    groupId: task.groupId,
    assignedAt: task.assignedAt,
    completedAt: task.completedAt,
    currentAssignedToUserId: task.currentAssignedToUserId,
    ownerType: task.ownerType,
    patientFamilyName: patient?.familyName ?? null,
    patientGivenName: patient?.givenName ?? null,
    patientId: patient?.id ?? null,
    previousTaskId: task.previousTaskId,
    taskPriority: task.priority,
    tags: task.tags ?? null,
    searchText: null,
    status: task.status,
    source: task.source,
    type: task.type,
    workflowCreatedAt: inboundFile?.createdAt || null,
    note: inboundFile?.note || null,
    orderDescription: task.patientOrder?.description ?? null,
    insurance: properCase(
      getCoverageDisplayName({
        coverage: patient ? getPatientPrimaryCoverage(patient) : null,
        includesPlan: false,
        includeSubscriberName: false,
      })
    ),
    documentClassificationSummary: null,
    documentClassificationSlugs: null,
  };
}
